import { useState } from "react";
import { UseFieldArrayReturn, UseFormReturn } from "react-hook-form";
import { homeService } from "@miplatform/api";
import { HTTP_STATUSES } from "@miplatform/constants";
import { useComponentState } from "@miplatform/hooks";
import { IAccountDrawResponse } from "@miplatform/types";
import {
  IAccountDraw,
  IAccountDrawsData,
} from "../../CustomerPricingDetails/CustomerPricingDetails.types";

export const useCustomerDrawsSetupTabViewModel = ({
  form,
  fieldArray,
}: {
  form: UseFormReturn<IAccountDrawsData>;
  fieldArray: UseFieldArrayReturn<IAccountDrawsData, "draws", "id">;
}) => {
  const [draws, setDraws] = useState<IAccountDrawResponse[]>([]);

  const { getValues, setValue, reset } = form;
  const { append, remove } = fieldArray;

  const stateManager = useComponentState();

  const handleCalculateTotal = (updatedDraws: Partial<IAccountDraw>[]) => {
    const total = Number.parseFloat(
      updatedDraws
        .reduce((acc, d) => acc + (d.percentage ? d.percentage : 0), 0)
        .toFixed(2)
    );
    setValue("drawTotal", total, { shouldDirty: true });
    return total;
  };

  const handleSetDraws = (draws: IAccountDrawResponse[]) => {
    setDraws(draws);
    const drawTotal = handleCalculateTotal(draws);
    reset({ draws, drawTotal });
  };

  const getDraws = async (accountId: string) => {
    return stateManager.run(async () => {
      const response = await homeService.draws.getAll({
        accountId,
        sort: "displayOrder asc",
      });
      if (response.status === HTTP_STATUSES.OK) {
        handleSetDraws(response.data);
      }
    });
  };

  const handleSaveDraws = async (
    data: IAccountDrawsData,
    accountId: string,
    resetToDefaults = false
  ) => {
    const filteredDraws: IAccountDraw[] = data.draws.map(
      (draw: IAccountDraw, index: number) => {
        return {
          ...draw,
          displayOrder: index,
        };
      }
    );
    return stateManager.run(async () => {
      const response = await homeService.draws.update({
        accountId,
        data: filteredDraws,
        params: { resetToDefaults },
        toastMessageConfig: {
          success: { text: "Successfully saved draws." },
        },
      });
      if (response.status === HTTP_STATUSES.OK) {
        handleSetDraws(response.data);
      }
      return response;
    });
  };

  const handleAddDraw = () => {
    append({
      name: "",
      displayOrder: getValues("draws").length,
      percentage: 0,
    });
  };

  const handleRemoveDraw = (index: number) => {
    remove(index);
    handleCalculateTotal(getValues("draws"));
  };

  return {
    draws,
    setDraws,
    getDraws,
    handleAddDraw,
    handleRemoveDraw,
    handleSaveDraws,
    handleCalculateTotal,
    isLoading: stateManager.isBusy(),
  };
};
